import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';
const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
};
export function Modal({ title, onClose, children, size = 'md', footer }) {
    const firstFocusRef = useRef(null);
    const panelRef = useRef(null);
    // Close on Escape
    useEffect(() => {
        const h = (e) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    }, [onClose]);
    // Focus the close button on mount; restore focus on unmount
    useEffect(() => {
        const prev = document.activeElement;
        firstFocusRef.current?.focus();
        // Scroll lock
        const prev_overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev_overflow;
            prev?.focus?.();
        };
    }, []);
    // Focus trap — keep Tab/Shift+Tab inside the modal
    const handleKeyDown = (e) => {
        if (e.key !== 'Tab' || !panelRef.current)
            return;
        const focusable = panelRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last?.focus();
        }
        else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first?.focus();
        }
    };
    return (_jsx("div", { className: "fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4", role: "dialog", "aria-modal": "true", "aria-labelledby": "modal-title", style: { background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }, onClick: e => e.target === e.currentTarget && onClose(), onKeyDown: handleKeyDown, children: _jsxs("div", { ref: panelRef, className: cn('bg-white w-full flex flex-col', 'rounded-t-2xl sm:rounded-2xl shadow-modal', 'max-h-[92vh] sm:max-h-[90vh]', 'animate-zoom-in', sizes[size]), children: [_jsxs("div", { className: "flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0", children: [_jsx("h2", { id: "modal-title", className: "text-base font-semibold text-gray-900 leading-tight", children: title }), _jsx("button", { ref: firstFocusRef, onClick: onClose, "aria-label": "Close modal", className: "w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors", children: _jsx("i", { className: "ti ti-x text-base", "aria-hidden": "true" }) })] }), _jsx("div", { className: "flex-1 overflow-y-auto p-6 scrollbar-thin", children: children }), footer && (_jsx("div", { className: "px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl shrink-0", children: footer }))] }) }));
}
